import React, { useState, useRef, useEffect } from 'react';
import { checkCredentials, startSession } from '../auth';
import './LoginScreen.css';

/**
 * Экран входа в админ-панель.
 *
 * Задержка после неверной попытки нужна, чтобы автоматический перебор был
 * непрактичным: пауза растёт с каждой неудачей, а после пяти подряд вход
 * блокируется на минуту.
 */
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60 * 1000;

export default function LoginScreen({ onSuccess }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const loginRef = useRef(null);

  useEffect(() => {
    if (loginRef.current) loginRef.current.focus();
  }, []);

  // Тикаем, пока действует блокировка, чтобы обновлять обратный отсчёт.
  useEffect(() => {
    if (lockedUntil <= now) return undefined;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [lockedUntil, now]);

  const lockLeft = Math.max(0, Math.ceil((lockedUntil - now) / 1000));
  const isLocked = lockLeft > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy || isLocked) return;

    setBusy(true);
    setError('');
    try {
      const ok = await checkCredentials(login, password);
      if (ok) {
        startSession();
        onSuccess();
        return;
      }

      const next = attempts + 1;
      setAttempts(next);
      setPassword('');

      if (next >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCKOUT_MS);
        setNow(Date.now());
        setAttempts(0);
        setError('Слишком много попыток. Вход заблокирован на минуту.');
      } else {
        // Небольшая пауза, растущая с числом неудач.
        await new Promise((r) => setTimeout(r, 300 * next));
        setError(`Неверный логин или пароль. Осталось попыток: ${MAX_ATTEMPTS - next}`);
      }
    } catch (err) {
      setError('Не удалось выполнить вход. Обновите страницу и попробуйте снова.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <h1 className="login-title">Админ-панель</h1>
        <p className="login-subtitle">itinterview</p>

        <label className="login-label" htmlFor="login-input">
          Логин
        </label>
        <input
          id="login-input"
          ref={loginRef}
          className="login-input"
          type="text"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          autoComplete="username"
          disabled={busy || isLocked}
          spellCheck="false"
        />

        <label className="login-label" htmlFor="password-input">
          Пароль
        </label>
        <input
          id="password-input"
          className="login-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          disabled={busy || isLocked}
        />

        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        <button className="login-button" type="submit" disabled={busy || isLocked}>
          {isLocked ? `Подождите ${lockLeft} с` : busy ? 'Проверка…' : 'Войти'}
        </button>
      </form>
    </div>
  );
}
